import { Pencil } from 'lucide-react';
import { useLayoutEffect, useRef, useState } from 'react';
import { cn } from '../../lib/utils.ts';
import { Button } from '../ui/button.tsx';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog.tsx';
import { Textarea } from '../ui/input.tsx';

/** Past this, or on more than one line, the text is long whatever the layout says (jsdom has none). */
const LONG = 200;

/**
 * The project's description under its name: two lines with an ellipsis and "See more" (which
 * unfolds it in place) when it runs longer, and Edit, which opens a dialog — the page never holds
 * an open text box. Without write access it is read-only; with no description and no write
 * access it is left out.
 */
export function ProjectDescription({
  description,
  canWrite,
  saving,
  onSave,
}: {
  description: string | null;
  canWrite: boolean;
  saving: boolean;
  /** Whether it saved; on a refusal (shown by the page) the dialog stays open with the draft. */
  onSave: (description: string | null) => Promise<boolean>;
}) {
  const text = description?.trim() ?? '';
  const ref = useRef<HTMLParagraphElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const [editing, setEditing] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setOverflows(el.scrollHeight > el.clientHeight + 1);
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(el);
    return () => observer?.disconnect();
  }, [text, expanded]);
  const long = overflows || text.length > LONG || text.includes('\n');

  if (!text && !canWrite) return null;
  return (
    <div className="flex flex-col items-start gap-1 text-sm" data-testid="project-description">
      {text ? (
        <p
          ref={ref}
          className={cn(
            'text-muted-foreground whitespace-pre-wrap break-words',
            !expanded && 'line-clamp-2',
          )}
          data-expanded={expanded}
        >
          {text}
        </p>
      ) : (
        <p className="text-muted-foreground italic">No description</p>
      )}
      <div className="flex items-center gap-3">
        {text && (long || expanded) && (
          <button
            type="button"
            className="text-brand text-xs font-medium hover:underline"
            aria-expanded={expanded}
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? 'See less' : 'See more'}
          </button>
        )}
        {canWrite && (
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-xs"
            onClick={() => setEditing(true)}
          >
            <Pencil className="size-3" /> {text ? 'Edit description' : 'Add description'}
          </button>
        )}
      </div>
      {editing && (
        <DescriptionDialog
          initial={description ?? ''}
          saving={saving}
          onClose={() => setEditing(false)}
          onSave={async (value) => {
            if (await onSave(value)) setEditing(false);
          }}
        />
      )}
    </div>
  );
}

function DescriptionDialog({
  initial,
  saving,
  onClose,
  onSave,
}: {
  initial: string;
  saving: boolean;
  onClose: () => void;
  onSave: (description: string | null) => Promise<void>;
}) {
  const [draft, setDraft] = useState(initial);
  const changed = draft.trim() !== initial.trim();
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Description</DialogTitle>
        </DialogHeader>
        <form
          id="project-description-form"
          onSubmit={(e) => {
            e.preventDefault();
            void onSave(draft.trim() || null);
          }}
        >
          <Textarea
            aria-label="Description"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="max-h-[60vh] min-h-40"
            autoFocus
          />
        </form>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="project-description-form" disabled={!changed || saving}>
            Save description
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
